// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

// 1. Crear el Contexto
export const AuthContext = createContext(null);

// 2. Crear un Proveedor de Contexto (AuthContextProvider)
export const AuthProvider = ({ children }) => {
  // Inicializa el estado del usuario y el token desde localStorage
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Error parsing stored user from localStorage", error);
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoggedIn, setIsLoggedIn] = useState(!!user && !!token);

  // Función para manejar el logout de manera segura y limpia
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    console.log('AuthContext: Usuario deslogueado por caducidad o acción manual.');
    // Recargar la página para limpiar la memoria de React y evitar "estados bugeados" de la sesión anterior
    window.location.href = '/';
  }, []);

  // Efecto para verificar si el token JWT ya expiró o programar su cierre automático
  useEffect(() => {
    if (token) {
      try {
        const payloadBase64 = token.split('.')[1];
        if (payloadBase64) {
          const payload = JSON.parse(atob(payloadBase64));
          if (payload.exp) {
            const expMs = payload.exp * 1000;
            const nowMs = Date.now();
            
            if (nowMs >= expMs) {
              console.warn('El token ya expiró. Forzando cierre de sesión.');
              logout();
            } else {
              // Si no ha expirado, programamos un temporizador para cerrarlo cuando llegue el momento
              const timeUntilExpiry = expMs - nowMs;
              const timeoutId = setTimeout(() => {
                console.warn('Tiempo de sesión agotado. Cerrando sesión.');
                logout();
              }, timeUntilExpiry);
              
              return () => clearTimeout(timeoutId); // Limpiar timeout si cambia el token
            }
          }
        }
      } catch (e) {
        console.error("No se pudo parsear el token para validar expiración.", e);
      }
    }
  }, [token, logout]);

  // Efecto para persistir el usuario y token en localStorage cuando cambian
  useEffect(() => {
    if (user && token) {
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);
      setIsLoggedIn(true);
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setIsLoggedIn(false);
    }
  }, [user, token]);

  // Función para manejar el login exitoso
  const login = (userData, jwtToken) => {
    setUser(userData);
    setToken(jwtToken);
    console.log('AuthContext: Usuario logueado y token establecido.');
  };

  // El valor que se proporcionará a los componentes que consuman el contexto
  const authContextValue = {
    user,
    token, // Proporcionar el token
    isLoggedIn,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// 3. Crear un Hook personalizado para consumir el contexto fácilmente
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
